import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { MdArrowBack, MdRefresh } from "react-icons/md";
import axios from "../../../axios";
import { toast } from "react-toastify";

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getListFromResponse = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;
  const candidates = [
    result,
    result?.users,
    result?.results,
    result?.attempts,
    result?.submissions,
    data?.users,
    data?.results,
    data?.attempts,
    data?.submissions,
    data,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
};

const getMetaFromResponse = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;

  return !Array.isArray(result) && typeof result === "object"
    ? result
    : !Array.isArray(data) && typeof data === "object"
    ? data
    : {};
};

const getUser = (row) =>
  row?.user ||
  row?.userId ||
  row?.student ||
  row?.studentId ||
  row?.candidate ||
  {};

const getUserId = (row) => {
  const user = getUser(row);
  const id = pickFirst(user?._id, user?.id, row?.userId, row?.user);

  return typeof id === "object" ? pickFirst(id?._id, id?.id) : id;
};

const getUserName = (row) => {
  const user = getUser(row);

  return pickFirst(
    row?.name,
    row?.userName,
    row?.studentName,
    user?.name,
    user?.fullName,
    "User",
  );
};

const getUserMobile = (row) => {
  const user = getUser(row);

  return pickFirst(row?.mobile, row?.phone, user?.mobile, user?.phone, "-");
};

const getUserEmail = (row) => {
  const user = getUser(row);

  return pickFirst(row?.email, user?.email, "-");
};

const formatDate = (date) => {
  if (!date) return "-";
  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatValue = (value, fallback = "-") =>
  value === undefined || value === null || value === "" ? fallback : value;

const getPayload = (response) =>
  response?.data?.result || response?.data?.data || response?.data || {};

const pickLeaderboardRows = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;
  const candidates = [
    result?.leaderboard,
    result?.users,
    result?.topUsers,
    result?.results,
    result?.attempts,
    result,
    data?.leaderboard,
    data?.users,
    data?.topUsers,
    data,
  ];

  return candidates.find((item) => Array.isArray(item)) || [];
};

const getLeaderScore = (row) =>
  pickFirst(row?.score, row?.marks, row?.totalScore, row?.finalScore, 0);

const pickAnswerRows = (payload) => {
  const candidates = [
    payload?.questions,
    payload?.answers,
    payload?.answerKey,
    payload?.records,
    payload?.items,
    payload?.result,
    payload?.data,
    Array.isArray(payload) ? payload : null,
  ];

  return candidates.find((item) => Array.isArray(item)) || [];
};

const normalizeText = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const normalizeForCompare = (value) => normalizeText(value).toLowerCase();

const getQuestionSource = (row) => {
  if (row?.question && typeof row.question === "string") return row;

  return (
    row?.question ||
    row?.mockQuestion ||
    row?.mockTestQuestion ||
    row?.questionData ||
    row
  );
};

const getQuestionText = (row, index) => {
  const source = getQuestionSource(row);

  return pickFirst(
    row?.questionText,
    source?.question,
    source?.questionEng,
    source?.questionHin,
    `Question ${index + 1}`,
  );
};

const getOptions = (row) => {
  const source = getQuestionSource(row);

  if (Array.isArray(source?.options)) return source.options;
  if (Array.isArray(row?.options)) return row.options;

  return [source?.optionA, source?.optionB, source?.optionC, source?.optionD].filter(
    (option) => option !== undefined && option !== null,
  );
};

const getSelectedAnswer = (row) =>
  pickFirst(
    row?.userAnswer,
    row?.selectedAnswer,
    row?.submittedAnswer,
    row?.answer,
    row?.selectedOption,
    "",
  );

const getCorrectAnswer = (row) => {
  const source = getQuestionSource(row);

  return pickFirst(
    row?.correctAnswer,
    row?.rightAnswer,
    row?.rightAnsEng,
    source?.answer,
    source?.correctAnswer,
    source?.answerHin,
    "",
  );
};

export default function MockTestResults() {
  const { mockTestId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isLeaderboardRoute = location.pathname.endsWith("/leaderboard");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [answerKeyRows, setAnswerKeyRows] = useState([]);
  const [answerKeyMeta, setAnswerKeyMeta] = useState({});
  const [answerKeyLoading, setAnswerKeyLoading] = useState(false);
  const [selectedAnswerUser, setSelectedAnswerUser] = useState(null);
  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [viewMode, setViewMode] = useState(
    isLeaderboardRoute ? "leaderboard" : "users",
  );

  const fetchResults = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`/mock-tests/${mockTestId}/results`);
      setRows(getListFromResponse(response));
      setMeta(getMetaFromResponse(response?.data?.result));
    } catch (error) {
      setRows([]);
      setMeta({});
      toast.error(
        error.response?.data?.message || "Mock test users load nahi ho paye",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockTestId]);

  const filteredRows = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) return rows;

    return rows.filter((row) =>
      [getUserName(row), getUserMobile(row), getUserEmail(row)]
        .join(" ")
        .toLowerCase()
        .includes(value),
    );
  }, [rows, search]);

  const stats = useMemo(() => {
    const submitted = rows.filter((row) =>
      Boolean(
        pickFirst(
          row?.submittedAt,
          row?.submittedOn,
          row?.completedAt,
          row?.isSubmitted,
          row?.status === "submitted",
          row?.status === "completed",
        ),
      ),
    ).length;

    const avgScore =
      rows.length === 0
        ? 0
        : rows.reduce(
            (sum, row) => sum + Number(pickFirst(row?.score, row?.marks, 0)),
            0,
          ) / rows.length;

    return {
      total: rows.length,
      submitted,
      pending: Math.max(rows.length - submitted, 0),
      avgScore: Number.isFinite(avgScore) ? avgScore.toFixed(2) : "0.00",
    };
  }, [rows]);

  const title =
    meta?.mockTestName ||
    "Mock Test";

  const renderStatus = (row) => {
    const status = pickFirst(
      row?.status,
      row?.isSubmitted ? "submitted" : "",
      row?.submittedAt || row?.submittedOn || row?.completedAt ? "submitted" : "",
      "pending",
    );
    const normalized = String(status).toLowerCase();
    const isSubmitted =
      normalized.includes("submit") || normalized.includes("complete");

    return (
      <span
        className={`rounded-full px-3 py-1 text-xs font-semibold ${
          isSubmitted
            ? "bg-green-100 text-green-700"
            : "bg-amber-100 text-amber-700"
        }`}
      >
        {isSubmitted ? "Submitted" : "Pending"}
      </span>
    );
  };

  const renderUserLink = (row) => {
    const userId = getUserId(row);
    const name = getUserName(row);

    if (!userId) return <span className="font-semibold">{name}</span>;

    return (
      <Link
        to={`/dash/view-user/${userId}`}
        className="font-semibold text-bgBlue hover:underline"
      >
        {name}
      </Link>
    );
  };

  const closeAnswerKey = () => {
    setSelectedAnswerUser(null);
    setAnswerKeyRows([]);
    setAnswerKeyMeta({});
  };

  const handleShowAnswerKey = async (row) => {
    const userId = getUserId(row);

    if (!userId) {
      toast.error("User id nahi mila");
      return;
    }

    setSelectedAnswerUser(row);
    setAnswerKeyRows([]);
    setAnswerKeyMeta({});
    setAnswerKeyLoading(true);

    try {
      const response = await axios.get(
        `/mock-tests/${mockTestId}/users/${userId}/answer-key`,
      );
      const payload = getPayload(response);

      setAnswerKeyRows(pickAnswerRows(payload));
      setAnswerKeyMeta(
        payload && !Array.isArray(payload) && typeof payload === "object"
          ? payload
          : {},
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Answer key load nahi ho payi",
      );
    } finally {
      setAnswerKeyLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    setViewMode("leaderboard");
    setLeaderboardLoading(true);

    try {
      const response = await axios.get(`/mock-tests/${mockTestId}/leaderboard`);
      setLeaderboardRows(pickLeaderboardRows(response));
    } catch (error) {
      setLeaderboardRows([]);
      toast.error(error.response?.data?.message || "Leaderboard load nahi ho paya");
    } finally {
      setLeaderboardLoading(false);
    }
  };

  useEffect(() => {
    if (isLeaderboardRoute) {
      fetchLeaderboard();
    } else {
      setViewMode("users");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeaderboardRoute, mockTestId]);

  const renderAnswerKey = () => {
    if (!selectedAnswerUser) return null;
    const answerKeyTitle = pickFirst(
      answerKeyMeta?.mockTestName,
      answerKeyMeta?.mockTest?.title,
      answerKeyMeta?.mockTest?.planName,
      title,
    );

    return (
      <div className="min-h-screen bg-bgWhite p-4 md:p-6">
        <div className="mx-auto max-w-5xl">
          <button
            type="button"
            className="mb-4 inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700"
            onClick={closeAnswerKey}
          >
            <MdArrowBack />
            Back to Users
          </button>

          <div className="mb-5 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div>
              <p className="text-xs font-bold uppercase text-purple">
                Mock Test Answer Key
              </p>
              <h2 className="mt-1 font-oswald text-2xl font-bold text-bgBlue">
                {answerKeyTitle}
              </h2>
              <div className="mt-3 grid gap-2 text-sm text-gray-600 sm:grid-cols-3">
                <p>
                  <b className="text-gray-800">User:</b>{" "}
                  {getUserName(selectedAnswerUser)}
                </p>
                <p>
                  <b className="text-gray-800">Mobile:</b>{" "}
                  {getUserMobile(selectedAnswerUser)}
                </p>
                <p>
                  <b className="text-gray-800">Email:</b>{" "}
                  {getUserEmail(selectedAnswerUser)}
                </p>
              </div>
            </div>
          </div>

          <div>
            {answerKeyLoading ? (
              <div className="rounded-lg border border-gray-200 bg-white py-10 text-center text-gray-500 shadow-sm">
                Loading answer key...
              </div>
            ) : answerKeyRows.length ? (
              <div className="space-y-4">
                {answerKeyRows.map((answerRow, index) => {
                  const selectedAnswer = normalizeText(getSelectedAnswer(answerRow));
                  const correctAnswer = normalizeText(getCorrectAnswer(answerRow));
                  const isSkipped = !selectedAnswer;
                  const isCorrect =
                    !isSkipped &&
                    (answerRow?.isCorrect === true ||
                      answerRow?.ansRight === true ||
                      normalizeForCompare(selectedAnswer) ===
                        normalizeForCompare(correctAnswer));
                  const options = getOptions(answerRow);

                  return (
                    <div
                      key={answerRow?._id || index}
                      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                    >
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-bold text-purple">
                          Question {index + 1}
                        </p>
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            isSkipped
                              ? "bg-gray-200 text-gray-700"
                              : isCorrect
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {isSkipped ? "Skipped" : isCorrect ? "Correct" : "Wrong"}
                        </span>
                      </div>

                      <p className="font-semibold text-gray-900">
                        {getQuestionText(answerRow, index)}
                      </p>

                      {options.length ? (
                        <div className="mt-3 grid gap-2 text-sm">
                          {options.map((option, optionIndex) => {
                            const optionText = normalizeText(option);
                            const matchesCorrect =
                              normalizeForCompare(optionText) ===
                              normalizeForCompare(correctAnswer);
                            const matchesSelected =
                              normalizeForCompare(optionText) ===
                              normalizeForCompare(selectedAnswer);

                            return (
                              <div
                                key={`${answerRow?._id || index}-${optionIndex}`}
                                className={`rounded border px-3 py-2 ${
                                  matchesCorrect
                                    ? "border-green-300 bg-green-50 text-green-800"
                                    : matchesSelected
                                    ? "border-red-300 bg-red-50 text-red-800"
                                    : "border-gray-200 bg-white text-gray-700"
                                }`}
                              >
                                <span className="font-bold">
                                  {String.fromCharCode(65 + optionIndex)}.
                                </span>{" "}
                                {optionText}
                                {matchesCorrect ? "" : ""}
                                {matchesSelected && !matchesCorrect
                                  ? "  "
                                  : ""}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="mt-3 grid gap-2 text-sm">
                          <p className="rounded bg-red-50 px-3 py-2 text-red-700">
                            User Answer: {selectedAnswer || "Skipped"}
                          </p>
                          <p className="rounded bg-green-50 px-3 py-2 text-green-700">
                            Correct Answer: {correctAnswer || "-"}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-white py-10 text-center text-gray-500 shadow-sm">
                Answer key nahi mili
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const answerKeyPage = renderAnswerKey();

  if (answerKeyPage) {
    return answerKeyPage;
  }

  if (viewMode === "leaderboard") {
    return (
      <div className="bg-bgWhite p-4 md:p-6">
        <button
          type="button"
          className="mb-4 inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700"
          onClick={() =>
            isLeaderboardRoute
              ? navigate(`/dash/mock-tests/${mockTestId}/results`)
              : setViewMode("users")
          }
        >
          <MdArrowBack />
          Back to Users
        </button>

        <div className="mb-5 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase text-purple">
              Mock Test Leaderboard
            </p>
            <h2 className="mt-1 font-oswald text-2xl font-bold text-bgBlue">
              {title}
            </h2>
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded bg-bgBlue px-4 py-2 text-sm font-semibold text-white"
            onClick={fetchLeaderboard}
          >
            <MdRefresh />
            Refresh
          </button>
        </div>

        {leaderboardLoading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-500 shadow-sm">
            Loading leaderboard...
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="grid grid-cols-[80px_1fr_120px_120px] bg-gray-100 px-4 py-3 text-xs font-bold uppercase text-gray-600">
              <span>Rank</span>
              <span>User</span>
              <span className="text-right">Score</span>
              <span className="text-right">Attempted</span>
            </div>
            {leaderboardRows.map((row, index) => (
              <div
                key={row?._id || getUserId(row) || index}
                className="grid grid-cols-[80px_1fr_120px_120px] border-t border-gray-100 px-4 py-3 text-sm"
              >
                <span className="font-bold text-purple">
                  #{pickFirst(row?.rank, index + 1)}
                </span>
                <span className="font-semibold text-gray-900">
                  {getUserName(row)}
                </span>
                <span className="text-right font-bold text-bgBlue">
                  {getLeaderScore(row)}
                </span>
                <span className="text-right text-gray-700">
                  {formatValue(pickFirst(row?.attempted, row?.attemptedQuestions, row?.answers?.length, 0))}
                </span>
              </div>
            ))}
            {!leaderboardRows.length && (
              <div className="px-4 py-10 text-center text-gray-500">
                Leaderboard abhi available nahi hai.
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-bgWhite p-4 md:p-6">
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <button
            type="button"
            className="mb-3 inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700"
            onClick={() => navigate(-1)}
          >
            <MdArrowBack />
            Back
          </button>
          <h2 className="font-oswald text-2xl font-bold text-purple">
            {title}
          </h2>
          <p className="text-sm text-gray-600">
            Users list from mock test result API.
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm sm:w-80"
            placeholder="Search user, mobile, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded bg-bgBlue px-4 py-2 text-sm font-semibold text-white"
            onClick={fetchResults}
          >
            <MdRefresh />
            Refresh
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded bg-purple px-4 py-2 text-sm font-semibold text-white"
            onClick={fetchLeaderboard}
          >
            Leaderboard
          </button>
        </div>
      </div>

      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ["Total Users", stats.total],
          ["Submitted", stats.submitted],
          ["Pending", stats.pending],
          ["Avg Score", stats.avgScore],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          >
            <p className="text-xs font-semibold uppercase text-gray-500">
              {label}
            </p>
            <p className="mt-1 text-2xl font-bold text-bgBlue">{value}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-500 shadow-sm">
          Loading mock test users...
        </div>
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm md:block">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-100 text-xs uppercase text-gray-600">
                <tr>
                  <th className="px-4 py-3">Sr No.</th>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Right</th>
                  <th className="px-4 py-3">Wrong</th>
                  <th className="px-4 py-3">Attempted</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Submitted At</th>
                  <th className="px-4 py-3">Answer Key</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, index) => (
                  <tr key={row?._id || `${getUserId(row)}-${index}`} className="border-t">
                    <td className="px-4 py-3">{index + 1}</td>
                    <td className="px-4 py-3">{renderUserLink(row)}</td>
                    <td className="px-4 py-3">{getUserMobile(row)}</td>
                    <td className="px-4 py-3">{getUserEmail(row)}</td>
                    <td className="px-4 py-3">
                      {formatValue(pickFirst(row?.score, row?.marks, row?.totalScore, row?.finalScore, 0))}
                    </td>
                    <td className="px-4 py-3">
                      {formatValue(pickFirst(row?.correct, row?.rightAnswers, row?.correctAnswers, row?.correctCount, 0))}
                    </td>
                    <td className="px-4 py-3">
                      {formatValue(pickFirst(row?.wrong, row?.wrongAnswers, row?.incorrectAnswers, row?.incorrectCount, 0))}
                    </td>
                    <td className="px-4 py-3">
                      {formatValue(pickFirst(row?.attempted, row?.attemptedQuestions, row?.totalAttempts, row?.answers?.length, 0))}
                    </td>
                    <td className="px-4 py-3">{renderStatus(row)}</td>
                    <td className="px-4 py-3">
                      {formatDate(pickFirst(row?.submittedAt, row?.submittedOn, row?.completedAt, row?.updatedAt))}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="rounded bg-purple px-3 py-2 text-xs font-semibold text-white"
                        onClick={() => handleShowAnswerKey(row)}
                      >
                        View Answer Key
                      </button>
                    </td>
                  </tr>
                ))}
                {!filteredRows.length && (
                  <tr>
                    <td className="px-4 py-6 text-center text-gray-500" colSpan="11">
                      No mock test users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden">
            {filteredRows.map((row, index) => (
              <div
                key={row?._id || `${getUserId(row)}-${index}`}
                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-500">
                      #{index + 1}
                    </p>
                    {renderUserLink(row)}
                    <p className="mt-1 text-sm text-gray-500">
                      {getUserMobile(row)}
                    </p>
                    <p className="text-sm text-gray-500">{getUserEmail(row)}</p>
                  </div>
                  {renderStatus(row)}
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded bg-gray-50 p-2">
                    <p className="text-gray-500">Score</p>
                    <b>{formatValue(pickFirst(row?.score, row?.marks, 0))}</b>
                  </div>
                  <div className="rounded bg-gray-50 p-2">
                    <p className="text-gray-500">Attempted</p>
                    <b>{formatValue(pickFirst(row?.attempted, row?.attemptedQuestions, row?.answers?.length, 0))}</b>
                  </div>
                  <div className="rounded bg-gray-50 p-2">
                    <p className="text-gray-500">Right</p>
                    <b>{formatValue(pickFirst(row?.rightCount, row?.rightAnswers, row?.correctAnswers, 0))}</b>
                  </div>
                  <div className="rounded bg-gray-50 p-2">
                    <p className="text-gray-500">Wrong</p>
                    <b>{formatValue(pickFirst(row?.wrongCount, row?.wrongAnswers, row?.incorrectAnswers, 0))}</b>
                  </div>
                </div>

                <p className="mt-3 text-xs font-semibold text-gray-500">
                  Submitted:{" "}
                  {formatDate(pickFirst(row?.submittedAt, row?.submittedOn, row?.completedAt, row?.updatedAt))}
                </p>
                <button
                  type="button"
                  className="mt-3 w-full rounded bg-purple px-3 py-2 text-sm font-semibold text-white"
                  onClick={() => handleShowAnswerKey(row)}
                >
                  View Answer Key
                </button>
              </div>
            ))}
            {!filteredRows.length && (
              <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-gray-500 shadow-sm">
                No mock test users found
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
